// ABOUTME: Resolves user-facing labels for workflow states and transitions.
// ABOUTME: Priority: i18n message (workflow.state.<code> / workflow.transition.<event> / workflow.transitionTo.<to>) > definition label > humanized code.

import type { IntlShape } from 'react-intl';

import type { WorkflowDefinition } from '../api/workflowApi';

type LabelIntl = Pick<IntlShape, 'messages' | 'formatMessage'>;

const workflowStateMessageId = (code: string) => `workflow.state.${code}`;
const workflowTransitionMessageId = (event: string) => `workflow.transition.${event}`;
const workflowTransitionToMessageId = (to: string) => `workflow.transitionTo.${to}`;

// Checked up front so a missing key falls through to the definition label, instead of going through
// formatMessage's defaultMessage (which would parse a free-text definition label as ICU).
function translated(intl: LabelIntl, id: string): string | undefined {
    return intl.messages[id] ? intl.formatMessage({ id }) : undefined;
}

const humanize = (code: string) => code.replaceAll('_', ' ');

export function resolveWorkflowStateLabel(
    intl: LabelIntl,
    workflow: WorkflowDefinition | null | undefined,
    code: string,
): string {
    return (
        translated(intl, workflowStateMessageId(code)) ||
        workflow?.states.find(s => s.code === code)?.name?.trim() ||
        humanize(code)
    );
}

/**
 * Label of the transition button from `from` to `to`. `event` narrows the match when known (blocked
 * transitions carry it); otherwise the transition is looked up by from/to in the definition.
 * Order: message by event, message by target state (portable across tenant event codes), transition
 * name, action name, then the target state's label, so a button is never blank.
 */
export function resolveWorkflowTransitionLabel(
    intl: LabelIntl,
    workflow: WorkflowDefinition | null | undefined,
    from: string,
    to: string,
    event?: string,
): string {
    const transition = workflow?.transitions.find(
        t => t.from === from && t.to === to && (!event || t.event === event),
    );
    const eventCode = event ?? transition?.event;
    return (
        (eventCode && translated(intl, workflowTransitionMessageId(eventCode))) ||
        translated(intl, workflowTransitionToMessageId(to)) ||
        transition?.name?.trim() ||
        transition?.action?.name?.trim() ||
        resolveWorkflowStateLabel(intl, workflow, to)
    );
}
